import * as React from 'react';
import { ChevronDown } from 'lucide-react';

import {
  DEFAULT_PHONE_COUNTRY_CODE,
  getPhoneCountryOption,
  getPhoneInputValue,
  normalizePhoneValue,
  OTHER_PHONE_COUNTRY_CODE,
  PHONE_COUNTRY_OPTIONS,
  parsePhoneValue,
  formatPhoneValue,
} from '../../lib/phone';
import { cn } from '../../lib/utils';
import { Input } from './input';

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'value'> {
  inputClassName?: string;
  label: string;
  onChange: (value: string) => void;
  selectClassName?: string;
  value: string;
}

export function PhoneInput({
  className,
  defaultValue,
  disabled,
  id,
  inputClassName,
  label,
  onChange,
  placeholder,
  selectClassName,
  value,
  ...props
}: PhoneInputProps) {
  const defaultCountryCode = React.useMemo(() => {
    const normalizedDefaultValue =
      typeof defaultValue === 'string' ? normalizePhoneValue(defaultValue) : null;
    const parsedDefaultValue = normalizedDefaultValue
      ? parsePhoneValue(normalizedDefaultValue)
      : parsePhoneValue(value);

    return parsedDefaultValue.countryCode === OTHER_PHONE_COUNTRY_CODE
      ? DEFAULT_PHONE_COUNTRY_CODE
      : parsedDefaultValue.countryCode;
  }, [defaultValue, value]);
  const [selectedCountryCode, setSelectedCountryCode] = React.useState(() =>
    parsePhoneValue(value, defaultCountryCode).countryCode
  );

  React.useEffect(() => {
    setSelectedCountryCode(parsePhoneValue(value, defaultCountryCode).countryCode);
  }, [defaultCountryCode, value]);

  const selectedCountry =
    getPhoneCountryOption(selectedCountryCode) ??
    getPhoneCountryOption(DEFAULT_PHONE_COUNTRY_CODE);
  const inputValue = getPhoneInputValue(value, selectedCountryCode, defaultCountryCode);
  const selectedCountryTitle = selectedCountry?.dialCode
    ? `${selectedCountry.label} (${selectedCountry.dialCode})`
    : selectedCountry?.label ?? 'Pais e indicativo';

  function handleCountryChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const nextCountryCode = event.target.value;
    const currentInputValue = getPhoneInputValue(value, selectedCountryCode, defaultCountryCode);

    setSelectedCountryCode(nextCountryCode);

    if (!currentInputValue.trim()) {
      onChange('');
      return;
    }

    if (nextCountryCode === OTHER_PHONE_COUNTRY_CODE) {
      const normalizedCurrentValue = normalizePhoneValue(value, defaultCountryCode);
      onChange(normalizedCurrentValue ?? currentInputValue);
      return;
    }

    onChange(formatPhoneValue(currentInputValue, nextCountryCode));
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = event.target.value;

    if (!nextValue.trim()) {
      onChange('');
      return;
    }

    if (selectedCountryCode === OTHER_PHONE_COUNTRY_CODE) {
      onChange(nextValue);
      return;
    }

    onChange(formatPhoneValue(nextValue, selectedCountryCode));
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row',
        className
      )}
    >
      <div className="relative min-w-0 sm:w-[7.5rem] sm:shrink-0">
        <select
          value={selectedCountryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          aria-label="Pais e indicativo"
          title={selectedCountryTitle}
          className={cn(
            'h-14 w-full appearance-none rounded-[1.35rem] border border-[#dfd4c6] bg-[#fffdf9] px-4 pr-10 text-base text-[#16323b] shadow-[0_14px_28px_rgba(50,40,27,0.08)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f766e]/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fffaf4] disabled:cursor-not-allowed disabled:bg-[#f5efe5] disabled:text-[#a19a8f]',
            selectClassName
          )}
        >
          {PHONE_COUNTRY_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.dialCode || 'Outro'}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8e8273]" />
      </div>

      <Input
        {...props}
        id={id}
        type="tel"
        value={inputValue}
        onChange={handleInputChange}
        disabled={disabled}
        aria-label={props['aria-label'] ?? label}
        className={cn('min-w-0 sm:flex-1', inputClassName)}
        placeholder={placeholder ?? selectedCountry?.example ?? '+351 912 345 678'}
      />
    </div>
  );
}
